/**
 * useBriefing — TanStack Query hook over GET /api/briefings/today (Wave 2).
 *
 * The backend (briefing.service) returns the already-assembled briefing —
 * a short warm Bokmål `summary` plus the structured `facts` it was built from
 * — and lazily generates it on first read of the day, so the tile always has
 * content even before the ~06:30 cron fires.
 *
 * Cadence: the briefing is a once-a-day artdefact, so a long staleTime + a
 * 30-min poll is plenty (the poll mostly just rolls it over to the next day's
 * row after midnight). No WS topic — nothing changes intra-day.
 */
import { useQuery } from '@tanstack/react-query';

export type BriefingFacts = {
  date: string;
  member: { short_name: string; role: string };
  events: Array<{ title: string; time: string | null; all_day: boolean }>;
  chores: Array<{ title: string; points: number }>;
  weather: {
    temperature: number;
    feels_like: number;
    symbol_code: string;
    precipitation_mm: number;
  } | null;
  streak: { current_streak: number };
};

export type BriefingResponse = {
  member_id: string;
  date: string;
  summary: string;
  source: 'ai' | 'fallback';
  generated_at: string;
  delivered_at: string | null;
  facts: BriefingFacts;
};

export function useBriefing() {
  return useQuery<BriefingResponse>({
    queryKey: ['briefing', 'today'],
    queryFn: async () => {
      const r = await fetch('/api/briefings/today', {
        credentials: 'same-origin',
      });
      if (!r.ok) throw new Error(`briefing_fetch_failed: ${r.status}`);
      return (await r.json()) as BriefingResponse;
    },
    staleTime: 10 * 60_000,
    refetchInterval: 30 * 60_000,
    retry: 1,
  });
}
